import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import {
    type AppliedFilters,
    type FilterValue,
    rangeEnds,
    rangeValue,
} from '@/components/FilterBar/filters';
import {
    isProductSortKey,
    PRODUCT_FILTER_KEYS,
    type ProductFilterKey,
    statusFromUrl,
    statusToUrl,
} from '@/features/products/productFilters';
import { ProductsPage, type ProductsSearch } from '@/features/products/ProductsPage';

/*
  The product register. The search, the applied filters, the sort and the page all ride the URL
  so a filtered view can be shared and a reload restores it (ALS-43). Every filter is an optional
  string here and nothing more; `.catch(undefined)` keeps a junk value from throwing the route.

  The choice filters carry ids into registers the route has not loaded (a brand, a supplier), so
  they are passed through when they have the shape of one and read against the list once the
  page has it; the text and date filters are checked here.

  The drawer's one "Created between" field rides the URL as `from` and `to`, the prototype's keys
  and the API's, so a link shared while they were two fields still lands on the same view.

  The URL carries what differs from the opening view (ALS-289). The register opens on Status:
  Active, so a plain link opens there and `archived=All` is what a link showing everything carries.
*/
const param = z.string().optional().catch(undefined);
/** An id into a register, carried as a number so the URL reads cat=4 rather than a quoted string. */
const idParam = z.coerce.number().int().positive().optional().catch(undefined);

const productsSearch = z.object({
    q: param,
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    sort: param,
    dir: param,
    code: param,
    description: param,
    style: param,
    brand: idParam,
    cat: idParam,
    cat2: idParam,
    supplier: idParam,
    country: idParam,
    archived: param,
    createdBy: idParam,
    from: param,
    to: param,
});

type RawSearch = z.infer<typeof productsSearch>;

const TEXT_KEYS: readonly ProductFilterKey[] = [
    PRODUCT_FILTER_KEYS.code,
    PRODUCT_FILTER_KEYS.description,
    PRODUCT_FILTER_KEYS.style,
];
const ID_KEYS: readonly ProductFilterKey[] = [
    PRODUCT_FILTER_KEYS.brand,
    PRODUCT_FILTER_KEYS.cat,
    PRODUCT_FILTER_KEYS.cat2,
    PRODUCT_FILTER_KEYS.supplier,
    PRODUCT_FILTER_KEYS.country,
    PRODUCT_FILTER_KEYS.createdBy,
];

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const idOf = (value: string | undefined) => (value === undefined ? undefined : Number(value));

/**
 * The applied set as the URL carries it, with anything that cannot be a value dropped. Status is
 * read and written apart (`statusFromUrl`, `statusToUrl`), because a URL without one means the
 * opening view's Active while a page's set without one means All.
 *
 * A filter value may also be a `multi` field's list of ticked values (ALS-52). No product filter
 * is a multi one, so `one` drops a list rather than stringifying it into "a,b", which is what
 * would silently happen if one were ever added.
 */
function filtersFrom(
    raw: Partial<Record<ProductFilterKey | 'from' | 'to', FilterValue | number | undefined>>,
): AppliedFilters<ProductFilterKey> {
    const one = (value: FilterValue | number | undefined) =>
        Array.isArray(value) ? '' : String(value ?? '').trim();
    const out: AppliedFilters<ProductFilterKey> = {};
    for (const key of TEXT_KEYS) {
        const value = one(raw[key]);
        if (value) out[key] = value;
    }
    const day = (value: string) => (ISO_DAY.test(value) ? value : undefined);
    const created = rangeValue(day(one(raw.from)), day(one(raw.to)));
    if (created) out.created = created;
    for (const key of ID_KEYS) {
        const value = one(raw[key]);
        if (value && /^\d+$/.test(value)) out[key] = value;
    }
    return out;
}

export const Route = createFileRoute('/_app/products/')({
    validateSearch: (search) => productsSearch.parse(search),
    component: ProductsRoute,
});

function ProductsRoute() {
    const raw = Route.useSearch();
    const navigate = useNavigate();

    const search = useMemo<ProductsSearch>(() => {
        const sort = raw.sort && isProductSortKey(raw.sort) ? raw.sort : null;
        const filters = filtersFrom(raw);
        const status = statusFromUrl(raw.archived);
        if (status) filters.archived = status;
        return {
            q: raw.q ?? '',
            filters,
            sort: sort ? { key: sort, direction: raw.dir === 'desc' ? 'desc' : 'asc' } : null,
            page: raw.page ?? 1,
        };
    }, [raw]);

    const onSearchChange = useCallback(
        (next: ProductsSearch) => {
            /*
              A filter value is a string or, for a `multi` field, the list of ticked values
              (ALS-52). The URL carries strings, and no product filter is a multi one, so this
              narrows rather than encodes.
            */
            const str = (value: FilterValue | undefined) =>
                typeof value === 'string' ? value : undefined;
            const { created, ...rest } = next.filters;
            const applied = filtersFrom({ ...rest, ...rangeEnds(str(created)) });
            const ends = rangeEnds(str(applied.created));
            const nextSearch: RawSearch = {
                q: next.q.trim() || undefined,
                page: next.page > 1 ? next.page : undefined,
                sort: next.sort?.key,
                dir: next.sort?.direction,
                code: str(applied.code),
                description: str(applied.description),
                style: str(applied.style),
                from: ends.from,
                to: ends.to,
                archived: statusToUrl(str(next.filters.archived)),
                // Ids travel as numbers, so the URL reads cat=4 and matches the API's categoryId=4.
                brand: idOf(str(applied.brand)),
                cat: idOf(str(applied.cat)),
                cat2: idOf(str(applied.cat2)),
                supplier: idOf(str(applied.supplier)),
                country: idOf(str(applied.country)),
                createdBy: idOf(str(applied.createdBy)),
            };
            void navigate({
                to: '/products',
                search: nextSearch,
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    return <ProductsPage search={search} onSearchChange={onSearchChange} />;
}
